// /home/labdoo/zitech-nexus/frontend/src/pages/admin/AdminJobsList.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI } from '../../services/api';
import './AdminJobsList.css';

// SVG Icons
const Icons = {
  Plus: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  View: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3L21 7L7 21H3V17L17 3Z" />
    </svg>
  ),
  Delete: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7H20" />
      <path d="M10 11V16" />
      <path d="M14 11V16" />
      <path d="M5 7L6 19C6 19.5304 6.21071 20.0391 6.58579 20.4142C6.96086 20.7893 7.46957 21 8 21H16C16.5304 21 17.0391 20.7893 17.4142 20.4142C17.7893 20.0391 18 19.5304 18 19L19 7" />
      <path d="M9 7L10 3H14L15 7" />
    </svg>
  ),
  Deactivate: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  ),
  Activate: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8V16M8 12H16" />
    </svg>
  ),
  Spinner: () => (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ajl-spinner-svg">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Applicants: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Empty: () => (
    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
};

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  job_type: string;
  experience_required: string;
  description: string;
  requirements: string[];
  is_active: boolean;
  created_at: string;
  applications_count?: number;
}

const AdminJobsList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filter, setFilter] = useState('all');
  const [fetchingCounts, setFetchingCounts] = useState<Record<number, boolean>>({});

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getAllJobs();
      const jobsData = response.data;
      
      // Fetch applications count for each job
      const jobsWithCounts = await Promise.all(
        jobsData.map(async (job: Job) => {
          try {
            setFetchingCounts(prev => ({ ...prev, [job.id]: true }));
            const applicationsResponse = await jobsAPI.getJobApplications(job.id);
            return {
              ...job,
              applications_count: applicationsResponse.data?.length || 0
            };
          } catch (error) {
            console.error(`Error fetching applications for job ${job.id}:`, error);
            return {
              ...job,
              applications_count: 0
            };
          } finally {
            setFetchingCounts(prev => ({ ...prev, [job.id]: false }));
          }
        })
      );
      
      setJobs(jobsWithCounts);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await jobsAPI.deleteJob(id);
        fetchJobs();
      } catch (error) {
        console.error('Error deleting job:', error);
        alert('Failed to delete job');
      }
    }
  };

  const toggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      await jobsAPI.updateJob(id, { is_active: !currentStatus });
      fetchJobs();
    } catch (error) {
      console.error('Error updating job status:', error);
      alert('Failed to update job status');
    }
  };

  const getJobTypeLabel = (type: string) => {
    switch(type) {
      case 'full_time': return 'Full-time';
      case 'part_time': return 'Part-time';
      case 'contract': return 'Contract';
      case 'remote': return 'Remote';
      default: return type;
    }
  };

  const getJobTypeColor = (type: string) => {
    switch(type) {
      case 'full_time': return '#0d47a1';
      case 'part_time': return '#10b981';
      case 'contract': return '#f59e0b';
      case 'remote': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const filteredJobs = filter === 'all' 
    ? jobs 
    : filter === 'active' 
      ? jobs.filter(j => j.is_active)
      : jobs.filter(j => !j.is_active);

  const stats = {
    total: jobs.length,
    active: jobs.filter(j => j.is_active).length,
    inactive: jobs.filter(j => !j.is_active).length,
    totalApplications: jobs.reduce((sum, job) => sum + (job.applications_count || 0), 0),
  };

  if (loading) {
    return (
      <div className="ajl-loading-container">
        <div className="ajl-spinner">
          <Icons.Spinner />
        </div>
        <p className="ajl-loading-text">Loading jobs...</p>
      </div>
    );
  }

  return (
    <div className="ajl-container">
      {/* Header */}
      <div className="ajl-header">
        <div className="ajl-header-left">
          <h1 className="ajl-title">Job Management</h1>
          <p className="ajl-subtitle">Manage your job listings and track applications</p>
        </div>
        <button 
          className="ajl-btn-create"
          onClick={() => navigate('/dashboard/admin/jobs/create')}
        >
          <Icons.Plus />
          Create New Job
        </button>
      </div>

      {/* Stats Bar */}
      <div className="ajl-stats-bar">
        <div className="ajl-stat-item">
          <span className="ajl-stat-label">Total Jobs</span>
          <span className="ajl-stat-value">{stats.total}</span>
        </div>
        <div className="ajl-stat-divider"></div>
        <div className="ajl-stat-item">
          <span className="ajl-stat-label">Active</span>
          <span className="ajl-stat-value ajl-stat-active">{stats.active}</span>
        </div>
        <div className="ajl-stat-divider"></div>
        <div className="ajl-stat-item">
          <span className="ajl-stat-label">Inactive</span>
          <span className="ajl-stat-value ajl-stat-inactive">{stats.inactive}</span>
        </div>
        <div className="ajl-stat-divider"></div>
        <div className="ajl-stat-item">
          <span className="ajl-stat-label">Total Applicants</span>
          <span className="ajl-stat-value ajl-stat-applicants">{stats.totalApplications}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="ajl-filters">
        <button 
          className={`ajl-filter-btn ${filter === 'all' ? 'ajl-filter-active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All Jobs
          <span className="ajl-filter-count">{stats.total}</span>
        </button>
        <button 
          className={`ajl-filter-btn ${filter === 'active' ? 'ajl-filter-active' : ''}`}
          onClick={() => setFilter('active')}
        >
          Active
          <span className="ajl-filter-count">{stats.active}</span>
        </button>
        <button 
          className={`ajl-filter-btn ${filter === 'inactive' ? 'ajl-filter-active' : ''}`}
          onClick={() => setFilter('inactive')}
        >
          Inactive
          <span className="ajl-filter-count">{stats.inactive}</span>
        </button>
      </div>

      {/* Table */}
      <div className="ajl-table-wrapper">
        <table className="ajl-table">
          <thead>
            <tr>
              <th className="ajl-col-id">ID</th>
              <th className="ajl-col-title">Job Title</th>
              <th className="ajl-col-dept">Department</th>
              <th className="ajl-col-location">Location</th>
              <th className="ajl-col-type">Type</th>
              <th className="ajl-col-exp">Experience</th>
              <th className="ajl-col-applicants">Applicants</th>
              <th className="ajl-col-status">Status</th>
              <th className="ajl-col-created">Created</th>
              <th className="ajl-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.length === 0 ? (
              <tr>
                <td colSpan={10} className="ajl-empty-state">
                  <Icons.Empty />
                  <p className="ajl-empty-text">No jobs found</p>
                  <p className="ajl-empty-subtext">Create your first job listing to get started</p>
                </td>
              </tr>
            ) : (
              filteredJobs.map((job) => (
                <tr key={job.id} className="ajl-table-row">
                  <td className="ajl-col-id">
                    <span className="ajl-id-badge">#{job.id}</span>
                  </td>
                  <td className="ajl-col-title">
                    <span className="ajl-job-title">{job.title}</span>
                  </td>
                  <td className="ajl-col-dept">
                    <span className="ajl-department">{job.department}</span>
                  </td>
                  <td className="ajl-col-location">
                    <span className="ajl-location">{job.location}</span>
                  </td>
                  <td className="ajl-col-type">
                    <span 
                      className="ajl-type-badge"
                      style={{ 
                        background: `${getJobTypeColor(job.job_type)}15`,
                        color: getJobTypeColor(job.job_type)
                      }}
                    >
                      {getJobTypeLabel(job.job_type)}
                    </span>
                  </td>
                  <td className="ajl-col-exp">
                    <span className="ajl-experience">{job.experience_required}</span>
                  </td>
                  <td className="ajl-col-applicants">
                    <button 
                      className="ajl-applicants-btn"
                      onClick={() => navigate(`/dashboard/admin/applications?jobId=${job.id}`)}
                      title="View Applicants for this job"
                      disabled={fetchingCounts[job.id]}
                    >
                      <Icons.Applicants />
                      {fetchingCounts[job.id] ? (
                        <span className="ajl-applicants-loading"></span>
                      ) : (
                        <span className="ajl-applicants-count">{job.applications_count || 0}</span>
                      )}
                    </button>
                  </td>
                  <td className="ajl-col-status">
                    <span className={`ajl-status-badge ${job.is_active ? 'ajl-status-active' : 'ajl-status-inactive'}`}>
                      <span className="ajl-status-dot"></span>
                      {job.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="ajl-col-created">
                    <span className="ajl-created-date">
                      {new Date(job.created_at).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </td>
                  <td className="ajl-col-actions">
                    <div className="ajl-actions">
                      <button 
                        className="ajl-action-btn ajl-action-view"
                        onClick={() => navigate(`/dashboard/admin/applications?jobId=${job.id}`)}
                        title="View Applications"
                      >
                        <Icons.View />
                      </button>
                      <button 
                        className="ajl-action-btn ajl-action-edit"
                        onClick={() => navigate(`/dashboard/admin/jobs/${job.id}/edit`)}
                        title="Edit Job"
                      >
                        <Icons.Edit />
                      </button>
                      <button 
                        className={`ajl-action-btn ${job.is_active ? 'ajl-action-deactivate' : 'ajl-action-activate'}`}
                        onClick={() => toggleStatus(job.id, job.is_active)}
                        title={job.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {job.is_active ? <Icons.Deactivate /> : <Icons.Activate />}
                      </button>
                      <button 
                        className="ajl-action-btn ajl-action-delete"
                        onClick={() => handleDelete(job.id)}
                        title="Delete Job"
                      >
                        <Icons.Delete />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminJobsList;